import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Container Components  
export const AdminContainer = styled.div`
  padding: 0;
  margin: 0;
  background: #f8fafc;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
`;

export const AdminHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  margin: 0 0 2rem 0;
  border-radius: 0 0 24px 24px;
  position: relative;
  top: 0;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-bottom: 1rem;
  }
`;

// Enhanced HeaderContent with mobile stacking
export const HeaderContent = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  @media (max-width: 640px) {
    gap: 0.75rem;
  }
`;

export const HeaderText = styled.div`
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.1rem;
    opacity: 0.9;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.5rem;
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
`;

// Enhanced ActionButton with mobile responsiveness
export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: ${props => 
    props.variant === 'danger' ? '#ef4444' :
    props.variant === 'secondary' ? '#6b7280' : '#3b82f6'
  };
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => 
      props.variant === 'danger' ? '#dc2626' :
      props.variant === 'secondary' ? '#4b5563' : '#2563eb'
    };
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  // Mobile responsiveness
  @media (max-width: 640px) {
    padding: 0.625rem 1rem;
    font-size: 0.8rem;
    
    .btn-text {
      display: none;
    }
    
    // Show only icon on mobile
    svg {
      margin: 0;
    }
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    min-width: 44px; // Touch target size
    justify-content: center;
  }
`;

export const MainContent = styled.div`
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

// Card Components
export const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

export const StatCard = styled(Card)`
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.accentColor || '#3b82f6'};
  }
`;

export const ChartCard = styled(Card)`
  /* Chart-specific styles can be added here */
`;

// Enhanced StatsGrid with mobile responsiveness - 4 per line on desktop
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

export const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => props.minWidth || '300px'}, 1fr));
  gap: ${props => props.gap || '1.5rem'};
  margin: ${props => props.margin || '0'};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

// Stat Components
export const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.bgColor || '#dbeafe'};
  color: ${props => props.iconColor || '#3b82f6'};
  font-size: 1.5rem;
`;

export const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
`;

export const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

// Chart Components
export const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
`;

export const ChartFilters = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f9fafb'};
  }
`;

// Activity Feed Components
export const ActivityFeed = styled(Card)`
  max-height: 400px;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 1rem 0;
  }
`;

export const ActivityList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

export const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8fafc;
  }
`;

export const ActivityIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.bgColor || '#dbeafe'};
  color: ${props => props.iconColor || '#3b82f6'};
  font-size: 0.875rem;
  flex-shrink: 0;
`;

export const ActivityContent = styled.div`
  flex: 1;
  
  .activity-text {
    font-size: 0.875rem;
    color: #374151;
    margin-bottom: 0.25rem;
  }
  
  .activity-time {
    font-size: 0.75rem;
    color: #6b7280;
  }
`;

// Alert Components
export const AlertsPanel = styled(Card)`
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 1rem 0;
  }
`;

export const AlertItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  background: ${props => {
    switch(props.type) {
      case 'error': return '#fef2f2';
      case 'warning': return '#fffbeb';
      case 'success': return '#f0fdf4';
      default: return '#f8fafc';
    }
  }};
  border: 1px solid ${props => {
    switch(props.type) {
      case 'error': return '#fecaca';
      case 'warning': return '#fed7aa';
      case 'success': return '#bbf7d0';
      default: return '#e2e8f0';
    }
  }};
`;

export const AlertIcon = styled.div`
  color: ${props => {
    switch(props.type) {
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#6b7280';
    }
  }};
  flex-shrink: 0;
`;

export const AlertText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  color: #374151;
`;

// Button Components
export const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const DangerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Quick Action Components
export const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

export const QuickAction = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  color: #374151;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
    color: #3b82f6;
  }
  
  .icon {
    font-size: 1.25rem;
    color: #3b82f6;
    flex-shrink: 0;
  }
  
  .text {
    font-weight: 500;
  }
`;

// Table Components
export const TableContainer = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

export const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    background: #f9fafb;
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
  }
  
  td {
    padding: 1rem;
    border-bottom: 1px solid #f3f4f6;
    color: #6b7280;
  }
  
  tbody tr {
    transition: background-color 0.2s ease;
    
    &:hover {
      background: #f9fafb;
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }
  
  @media (max-width: 768px) {
    th, td {
      padding: 0.75rem 0.5rem;
      font-size: 0.875rem;
    }
  }
`;

// Form Components
export const FormContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

// Status Components
export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch(props.status) {
      case 'confirmed':
      case 'success':
      case 'active':
        return `
          background: #dcfce7;
          color: #166534;
        `;
      case 'pending':
      case 'warning':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      case 'error':
      case 'failed':
      case 'expired':
        return `
          background: #fee2e2;
          color: #991b1b;
        `;
      case 'info':
        return `
          background: #dbeafe;
          color: #1e40af;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

// Loading and Error Components
export const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  font-size: 1.1rem;
  color: #6b7280;
  
  .spinner {
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const ErrorMessage = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: center;
  border: 1px solid #fecaca;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    color: #374151;
  }
  
  p {
    margin: 0;
    max-width: 400px;
  }
`;

// Utility Components
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 2rem 0;
`;

export const Spacer = styled.div`
  height: ${props => props.height || '1rem'};
  width: ${props => props.width || 'auto'};
`;

// Chart Colors
export const CHART_COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899',
  indigo: '#6366f1',
  teal: '#14b8a6'
};

export const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Mobile-friendly SearchAndFilters
export const SearchAndFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  @media (max-width: 640px) {
    margin-bottom: 1rem;
  }
`;

// Enhanced SearchBox for mobile
export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0 0.75rem;
  gap: 0.5rem;
  flex: 1;
  min-width: 240px;

  svg {
    color: #9ca3af;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    padding: 0.75rem 0;
    font-size: 0.875rem;
    background: transparent;

    &::placeholder {
      color: #9ca3af;
    }
  }

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
    
    input {
      padding: 0.625rem 0;
      font-size: 16px; // Prevent zoom on iOS
    }
  }
`;

// Mobile-responsive FilterSelect
export const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.625rem;
    font-size: 16px; // Prevent zoom on iOS
  }
`;

export const ActionButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
`;

// Enhanced Table Components
export const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  // Horizontal scroll container for mobile
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
    
    thead, tbody, tr, td, th {
      display: block;
    }
    
    // Hide on mobile - cards will be used instead
    display: none;
  }

  thead {
    background: #f9fafb;
    
    th {
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      color: #374151;
      font-size: 0.875rem;
      border-bottom: 1px solid #e5e7eb;
      
      &:first-child {
        border-top-left-radius: 0.75rem;
      }
      
      &:last-child {
        border-top-right-radius: 0.75rem;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f3f4f6;
      transition: background-color 0.2s ease;
      
      &:hover {
        background: #f9fafb;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      td {
        padding: 1rem;
        font-size: 0.875rem;
        color: #374151;
        vertical-align: middle;
        
        &:first-child {
          font-weight: 500;
          color: #111827;
        }
      }
    }
  }

  // Common table cell styles
  .code-cell {
    font-family: 'Monaco', 'Consolas', monospace;
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    color: #374151;
  }

  .payment-code-cell {
    font-family: 'Monaco', 'Consolas', monospace;
    background: #eff6ff;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    color: #1e40af;
  }

  .amount-cell {
    display: flex;
    flex-direction: column;
    
    .amount {
      font-weight: 600;
      color: #374151;
    }
    
    .currency {
      font-size: 0.75rem;
      color: #6b7280;
      text-transform: uppercase;
    }
  }

  .actions-cell {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  // Mobile responsive styles
  @media (max-width: 768px) {
    th {
      display: none;
    }

    td {
      display: block;
      padding: 0.5rem 1rem;
      border-bottom: none;

      &:before {
        content: attr(data-label) ": ";
        font-weight: 600;
        color: #374151;
      }
    }

    tbody tr {
      display: block;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      margin-bottom: 1rem;
      background: white;
    }

    .actions-cell {
      justify-content: center;
      padding-top: 1rem;
      border-top: 1px solid #e5e7eb;
      margin-top: 0.5rem;
    }
  }
`;

// Table Action Button Components
export const TableActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: ${props => 
    props.variant === 'danger' ? '#fee2e2' : '#f3f4f6'
  };
  color: ${props => 
    props.variant === 'danger' ? '#dc2626' : '#6b7280'
  };
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  min-height: 32px;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: ${props => 
      props.variant === 'danger' ? '#fecaca' : '#e5e7eb'
    };
    color: ${props => 
      props.variant === 'danger' ? '#b91c1c' : '#4b5563'
    };
  }

  @media (max-width: 480px) {
    min-width: 44px; // Touch target size
    min-height: 44px;
    padding: 0.75rem;
    
    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

// Modal Components (shared across admin pages)
export const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

  h2 {
    margin: 0 0 1.5rem;
    font-size: 1.8rem;
    color: #1f2937;
    font-weight: 700;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

// Results Counter Component
export const ResultsCounter = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
    font-weight: 500;
`; 
